import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
} from "@mui/material";
import api from "../../api/client";
import { useSnackbar } from "../../providers/SnackbarProvider";
import BranchIcon from "@mui/icons-material/AccountTree";
import { useChangeableItems, getLabelByKey } from "../../store/useDynamicLabel";

const CreateBatch = ({ open, onClose, onSuccess, BatchId }) => {

  const changeableItems = useChangeableItems();
  const getLabel = (itemKey) => getLabelByKey(changeableItems, itemKey);

  const [loading, setLoading] = useState(false);
  const [institutes, setInstitutes] = useState([]);
  const [role, setRole] = useState(localStorage.getItem("role"));
  const [userData, setUserData] = useState(JSON.parse(localStorage.getItem("userData")) || {});
  const [isEditMode, setIsEditMode] = useState(false);

  const statusOptions = [
    { label: "Active", value: "ACTIVE" },
    { label: "Inactive", value: "INACTIVE" },
  ];

  const schema = Yup.object().shape({
    batchName: Yup.string()
      .required(`Please enter the ${getLabel('BATCHES')} name`)
      .min(3, `${getLabel('BATCHES')} name must be at least 3 characters`)
      .max(100, `${getLabel('BATCHES')} name must not exceed 100 characters`)
      .matches(/^[a-zA-Z0-9\s\-_+]+$/, `${getLabel('BATCHES')} name can only contain letters, numbers, spaces, hyphens, underscores, and plus signs`)
      .trim(),
    status: isEditMode
      ? Yup.object().required("Status is required")
      : Yup.object().nullable(),
    institute: role === "SUPER_ADMIN"
      ? Yup.object().required("Please select an institute")
      : Yup.object().nullable(),
  });

  const commonTextFieldStyle = {
    "& .MuiOutlinedInput-root": {
      "& fieldset": { borderColor: "white" },
      "&:hover fieldset": { borderColor: "#d1d1d1" },
      "&.Mui-focused fieldset": {
        borderColor: "#1c97e3",
      },
    },
  };

  const { showSnackbar } = useSnackbar();
  const {
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      batchName: "",
      status: null,
      institute: null,
    },
  });

  const instituteDropDown = async () => {
    try {
      const { data } = await api.post("ListInstituteNames");
      setInstitutes(data?.data ?? []);
    } catch (error) {
      console.error("Error fetching institutes:", error);
    }
  };

  const fetchBatchById = async (batchId) => {
    try {
      const res = await api.post("GetBatchById", {
        batch_id: batchId,
      });
      const batchData = res?.data?.data;
      if (batchData) {
        setValue("batchName", batchData.batch_name);
        setValue("status", statusOptions.find(s => s.label === batchData.status) || null);
        if (role === "SUPER_ADMIN") {
          setValue("institute", { institute_id: batchData.institute_id, name: batchData.institute_name });
        }
      }
    } catch (error) {
      console.error("Error fetching batch by ID:", error);
    }
  };

  useEffect(() => {
    if (role === "SUPER_ADMIN") {
      instituteDropDown();
    }
  }, [role]);

  useEffect(() => {
    if (open && BatchId) {
      setIsEditMode(true);
      fetchBatchById(BatchId);
    } else {
      setIsEditMode(false);
      reset();
    }
  }, [open, BatchId, reset]);

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const payload = {
        batch_name: data.batchName,
        status: isEditMode ? data.status?.label || "" : "Active",
        institute_id: role === "SUPER_ADMIN" ? data.institute?.institute_id : userData?.institute_id,
      };

      if (isEditMode) {
        payload.batch_id = BatchId;
      }

      console.log(isEditMode ? "Updating batch with payload:" : "Creating batch with payload:", payload);

      const res = await api.post(isEditMode ? "UpdateBatch" : "CreateBatch", payload);

      if (res?.data?.status === "success") {
        showSnackbar("success", isEditMode ? "Batch updated successfully" : "Batch created successfully");
        reset();
        onClose();
        if (onSuccess) onSuccess();
      } else {
        showSnackbar("error", res?.data?.message || `Failed to ${isEditMode ? "update" : "create"} batch`);
      }
    } catch (error) {
      console.log("Error:", error);
      showSnackbar("error", `Failed to ${isEditMode ? "update" : "create"} batch`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
          >
            <BranchIcon sx={{ fontSize: "32px", color: "#1976d2" }} />
          </Box>
          <Typography sx={{ fontSize: "24px", fontWeight: "bold" }}>
            {isEditMode ? `Update ${getLabel('BATCHES')}` : `Create ${getLabel('BATCHES')}`}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              rowGap: 2,
              pt: 2,
            }}
          >
            <Grid sx={{ width: "100%" }}>
              <Typography>
                {getLabel('BATCHES')} Name <span style={{ color: "red" }}>*</span>
              </Typography>
              <Controller
                name="batchName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    error={!!errors.batchName}
                    helperText={errors.batchName?.message}
                    placeholder={`Enter ${getLabel('BATCHES')} name`}
                    sx={{ ...commonTextFieldStyle, backgroundColor: "#f8f9fa", borderRadius: "8px" }}
                  />
                )}
              />
            </Grid>

            {isEditMode && (
              <Grid sx={{ width: "100%" }}>
                {/* Status Dropdown */}
                <Typography>
                  Status <span style={{ color: "red" }}>*</span>
                </Typography>
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      options={statusOptions}
                      getOptionLabel={(option) => option?.label || ""}
                      isOptionEqualToValue={(option, value) => option.value === value?.value}
                      value={field.value}
                      onChange={(_, value) => field.onChange(value)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select status"
                          error={!!errors.status}
                          helperText={errors.status?.message}
                          sx={{ ...commonTextFieldStyle, backgroundColor: "#f8f9fa", borderRadius: "8px" }}
                        />
                      )}
                    />
                  )}
                />
              </Grid>
            )}

            {role === "SUPER_ADMIN" && (
              <Grid sx={{ width: "100%" }}>
                {/* Institute Dropdown */}
                <Typography>
                  Institute <span style={{ color: "red" }}>*</span>
                </Typography>
                <Controller
                  name="institute"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      options={institutes}
                      getOptionLabel={(option) => option?.name || ""}
                      isOptionEqualToValue={(option, value) => option.institute_id === value?.institute_id}
                      value={field.value}
                      onChange={(_, value) => field.onChange(value)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select institute"
                          error={!!errors.institute}
                          helperText={errors.institute?.message}
                          sx={{ ...commonTextFieldStyle, backgroundColor: "#f8f9fa", borderRadius: "8px" }}
                        />
                      )}
                    />
                  )}
                />
              </Grid>
            )}
          </Box>
        </form>
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button
          variant="outlined"
          color="secondary"
          onClick={onClose}
          sx={{ textTransform: "none", borderRadius: "12px", px: 3 }}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={loading}
          onClick={handleSubmit(onSubmit)}
          sx={{
            textTransform: "none",
            borderRadius: "12px",
            px: 4,
            fontWeight: 600,
          }}
        >
          {loading ? (isEditMode ? "Updating..." : "Creating...") : (isEditMode ? "Update" : ` Create ${getLabel('BATCHES')}`)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateBatch;
